/** @jsxRuntime classic */
/** @jsx jsx */
// eslint-disable-next-line no-unused-vars
import React, { useRef, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { css, jsx } from '@emotion/react';
import { Link, useLocation } from 'react-router-dom';

import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import HomeIcon from '@mui/icons-material/Home';
import InsertInvitationOutlinedIcon from '@mui/icons-material/InsertInvitationOutlined';
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import ChatIcon from '@mui/icons-material/Chat';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SearchIcon from '@mui/icons-material/Search';
import UserOutlinedIcon from '@mui/icons-material/PersonOutline';
import UserIcon from '@mui/icons-material/Person';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import IntegrationIconOutlined from '@mui/icons-material/ExtensionOutlined';
import IntegrationIcon from '@mui/icons-material/Extension';

import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import SwipableDrawer from '@mui/material/SwipeableDrawer';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import Text from '@mui/material/Typography';

import SearchModal from './common/SearchModal';
import CommonDrawer from './common/Drawer';
import AccountLimits from './workspace/AccountLimits';
import { track } from '../utils/track';
import { useUserQuery } from '../actions/UserActions';
import Profile from './aside/Profile';
import UploadMenu from './upload/UploadMenu';
import { IS_SINGLE_TENENT_MODE } from '../constants';

function MenuLink({
  title,
  to,
  icon: Icon,
  filledIcon: FilledIcon,
  iconOnly = false,
  isNew = false,
  isImportant = false,
  onClick = null,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const active =
    (to !== '/' && location.pathname.startsWith(to)) ||
    (to === '/' && location.pathname === '/') ||
    (to === '/projects' &&
      (location.pathname === '/projects' ||
        location.pathname.startsWith('/folders')));
  let color = 'rgba(0, 0, 0, 0.60)';
  if (isImportant) {
    color = 'blue';
  } else if (active) {
    color = 'var(--text-primary)';
  }

  let currentIcon = active ? (
    <FilledIcon color="primary" />
  ) : (
    <Icon color="inherit" />
  );

  if (isImportant) {
    currentIcon = active ? (
      <FilledIcon color="primary" />
    ) : (
      <Icon color="primary" />
    );
  }

  const content = (
    <Link
      to={to}
      onClick={(e) => {
        if (to === '#') {
          e.preventDefault();
        }
        track('menu_click', { menu: title });
        if (typeof onClick === 'function') {
          onClick();
        }
      }}
      css={css`
        height: 56px;
        display: flex;
        color: var(--text-primary);
        text-decoration: none;
        font-weight: 500;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        box-sizing: border-box;
        width: 100%;

        color: ${color};
        transition: color 0.3s ease-in-out;
      `}
    >
      <ListItemButton
        css={css`
          height: 48px;
          padding: 0.5rem 0;
          flex-direction: column;
          align-items: center;
          box-sizing: border-box;
          width: 100%;
          gap: 4px;
          background: transparent !important;
          &:hover svg {
            transform: scale(1.1);
            transition: transform 0.1s ease-in-out;
          }

          &:active svg {
            transform: scale(1);
            transition: transform 0.1s ease-in-out;
          }

          &:hover > div {
            background: #dce8ff;
          }

          &:hover p {
            font-weight: 700 !important;
          }

          &:active p {
            font-weight: 400 !important;
          }

          ${active ? `& > div { background: #fff; }` : ''}
        `}
        disableRipple
      >
        <div
          css={css`
            width: 100%;
            height: 32px;
            border-radius: 64px;
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 4px;
            box-sizing: border-box;
          `}
        >
          {isNew ? (
            <Badge color="secondary" badgeContent={t('Ny')} variant="string">
              {currentIcon}
            </Badge>
          ) : (
            currentIcon
          )}
        </div>
        {!iconOnly ? (
          <Text
            css={css`
              font-size: 0.688rem;
              line-heght: 12px;
              letter-spacing: 0.15px;
              ${active ? `color: var(--primary-main); font-weight: 700;` : ''}
            `}
          >
            {title}
          </Text>
        ) : null}
      </ListItemButton>
    </Link>
  );
  if (iconOnly) {
    return (
      <Tooltip title={title} placement="right">
        {content}
      </Tooltip>
    );
  }
  return content;
}

export default function Aside({
  isMobile,
  isTablet,
  hideMenu,
  setHideMenu,
  minimizedMenu,
  guestPage = false,
}) {
  const { t } = useTranslation();
  const [searchOpen, setSearchOpen] = useState(false);
  const { user } = useUserQuery();
  const accountLimitsRef = useRef(null);

  const Drawer = isMobile ? SwipableDrawer : CommonDrawer;

  const iconOnly = false;

  let o = {};
  if (isMobile) {
    o.onOpen = () => setHideMenu(false);
  }

  useEffect(() => {
    if (isMobile && window.Intercom) {
      window.Intercom('update', {
        hide_default_launcher: hideMenu,
      });
    } else if (window.Intercom) {
      window.Intercom('update', {
        hide_default_launcher: false,
      });
    }
  }, [isMobile, hideMenu, guestPage]);

  return (
    <>
      <Drawer
        css={css`
          overflow-x: visible;

          .MuiDrawer-paper {
            overflow: visible;
          }
        `}
        variant={!isMobile ? 'permanent' : null}
        anchor="left"
        open={!isMobile ? !minimizedMenu : !hideMenu}
        onClose={() => setHideMenu(true)}
        {...o}
      >
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            position: sticky;
            top: 0;
            left: 0;
            right: 0;
            z-index: 100;
            flex-direction: column;
            height: calc(100% - var(--intercom-bubble-height));
            box-sizing: border-box;
            align-items: center;
            padding: ${isMobile ? '0.5rem' : '0'};
            gap: 4px;
          `}
        >
          <Link
            to="/"
            css={css`
              color: black;
              text-decoration: none;
              margin-left: 0;
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            <img
              src={'/aside-logo.svg'}
              alt={t('Logo')}
              css={css`
                height: 48px;
              `}
            />
            {IS_SINGLE_TENENT_MODE ? (
              <img
                src={'/tenent_logo_short.jpg'}
                alt={t('Logo')}
                css={css`
                  height: 48px;
                `}
              />
            ) : null}
          </Link>
          <div
            css={css`
              display: flex;
              flex-grow: 1;
              flex-direction: column;
            `}
          >
            <div
              css={css`
                box-sizing: border-box;
                border-radius: 8px;
                padding: 8px 0;

                & .MuiListItemText-primary {
                  font-size: 15px;
                  font-weight: 700;
                  letter-spacing: 0.15px;
                }
              `}
            >
              {!guestPage ? (
                <List
                  component="nav"
                  sx={{ padding: 0, paddingTop: 1, paddingBottom: 1 }}
                  css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    align-items: center;
                  `}
                >
                  <UploadMenu variant="small" />
                  <MenuLink
                    to="#"
                    onClick={() => setSearchOpen(true)}
                    icon={SearchOutlinedIcon}
                    filledIcon={SearchIcon}
                    title={t('Sök')}
                    iconOnly={iconOnly}
                  />
                  <div
                    css={css`
                      display: flex;
                      width: 24px;
                      margin-top: 12px;
                      flex-direction: column;
                      align-items: flex-start;
                      gap: 10px;
                      height: 1px;
                      background: rgba(0, 0, 0, 0.12);
                    `}
                  />
                  <MenuLink
                    to="/"
                    icon={HomeOutlinedIcon}
                    filledIcon={HomeIcon}
                    title={t('Hem')}
                    iconOnly={iconOnly}
                  />
                  <MenuLink
                    to="/thread"
                    icon={ChatOutlinedIcon}
                    iconOnly={iconOnly}
                    filledIcon={ChatIcon}
                    title={t('Fråga AI')}
                  />
                  {/* TODO add check on something */}
                  <MenuLink
                    to="/flow"
                    icon={AccountTreeOutlinedIcon}
                    iconOnly={iconOnly}
                    filledIcon={AccountTreeIcon}
                    title={t('Flöden')}
                  />
                  {user?.workspace?.allow_meeting_bot && (
                    <MenuLink
                      to="/meetings"
                      icon={CalendarTodayOutlinedIcon}
                      iconOnly={iconOnly}
                      filledIcon={CalendarTodayIcon}
                      title={t('Möten')}
                    />
                  )}
                  {user?.integrations?.length ? (
                    <MenuLink
                      to="/booking"
                      icon={InsertInvitationOutlinedIcon}
                      iconOnly={iconOnly}
                      filledIcon={InsertInvitationIcon}
                      title={t('Bokningar')}
                    />
                  ) : null}
                  {user.workspace_role === 'admin' && (
                    <MenuLink
                      to="/workspace/admin/members"
                      icon={PersonAddOutlinedIcon}
                      filledIcon={PersonAddIcon}
                      iconOnly={iconOnly}
                      title={t('Bjud in')}
                    />
                  )}
                  <MenuLink
                    to="/integrations"
                    icon={IntegrationIconOutlined}
                    filledIcon={IntegrationIcon}
                    iconOnly={iconOnly}
                    title={t('Integrationer')}
                  />
                </List>
              ) : (
                <List
                  component="nav"
                  sx={{ padding: 0, paddingTop: 1, paddingBottom: 1 }}
                  css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    align-items: center;
                  `}
                >
                  <MenuLink
                    to="/signup"
                    icon={PersonOutlinedIcon}
                    filledIcon={HomeIcon}
                    title={t('Skapa konto')}
                    iconOnly={iconOnly}
                  />
                </List>
              )}
            </div>
            {!guestPage ? (
              <div
                css={css`
                  margin-top: auto;
                `}
              >
                <Profile user={user} />
                <div
                  ref={accountLimitsRef}
                  css={css`
                    padding-top: 8px;
                    margin-bottom: 8px;
                  `}
                >
                  <AccountLimits />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </Drawer>
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
